import { useEffect, useMemo, useState } from 'react';
import type { UseActiveMaintenanceResult } from '../../hooks/useActiveMaintenance';
import { colors } from '../../styles/theme';

/** Format ms remaining as "HHh MMm SSs" (or "MMm SSs", or "SSs"). */
function formatCountdown(ms: number): string {
  if (ms <= 0) return '0s';
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

interface BannerCopy {
  prefix: string;
  suffix: string;
}

interface Props {
  data: UseActiveMaintenanceResult;
}

/**
 * Maintenance banner with typewriter intro and live countdown.
 *
 * UX rules (locked):
 *  - Sits above the NavBar (parent layout owns positioning).
 *  - Single light/cream background — never escalates color, even at T=0.
 *  - Same font as the rest of the app.
 *  - Static text types in character-by-character; once typing finishes
 *    only the countdown span re-renders.
 *  - No dismiss button.
 *
 * Returns null when there's no active window so the layout doesn't
 * reserve space for an empty banner.
 */
const MaintenanceBanner = ({ data }: Props): JSX.Element | null => {
  const { window: w, msUntilStart, startedNow } = data;

  const copy = useMemo<BannerCopy | null>(() => {
    if (!w) return null;
    if (startedNow) {
      return {
        prefix: `Maintenance in progress — ${w.reason_short}. Sessions will end shortly`,
        suffix: '.',
      };
    }
    return {
      prefix: `Scheduled maintenance — ${w.reason_short}. Begins in `,
      suffix: '. Please save your work.',
    };
  }, [w, startedNow]);

  const [typedPrefix, setTypedPrefix] = useState('');
  const [typedSuffix, setTypedSuffix] = useState('');
  const [prefixDone, setPrefixDone] = useState(false);

  useEffect(() => {
    if (!copy) return;
    setTypedPrefix('');
    setTypedSuffix('');
    setPrefixDone(false);

    let i = 0;
    let phase: 'prefix' | 'suffix' = 'prefix';
    const id = setInterval(() => {
      if (phase === 'prefix') {
        i += 1;
        setTypedPrefix(copy.prefix.slice(0, i));
        if (i >= copy.prefix.length) {
          setPrefixDone(true);
          phase = 'suffix';
          i = 0;
        }
      } else {
        i += 1;
        setTypedSuffix(copy.suffix.slice(0, i));
        if (i >= copy.suffix.length) clearInterval(id);
      }
    }, 28);

    return () => clearInterval(id);
  }, [copy]);

  if (!w || !copy) return null;

  const showCountdown = !startedNow && prefixDone;
  const countdownText = formatCountdown(msUntilStart ?? 0);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: '#FFF7E6',
        borderBottom: `1px solid ${colors.borderSoft}`,
        color: colors.ink,
      }}
    >
      <div className="max-w-[1440px] mx-auto px-6 py-2 flex items-center gap-3 text-sm">
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: colors.warning,
            flexShrink: 0,
          }}
        />
        <p className="leading-tight" style={{ fontFamily: 'inherit' }}>
          <span>{typedPrefix}</span>
          {showCountdown && (
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
              {countdownText}
            </span>
          )}
          <span>{typedSuffix}</span>
        </p>
      </div>
    </div>
  );
};

export default MaintenanceBanner;
